import { useListeningPermissions } from '@/app/socialListening/composables/useListeningPermissions';
import { useOrganizationSentiment } from '@/app/socialListening/composables/useOrganizationSentiment';
import {
  formatSentimentAdjustmentTooltip,
  getPostSentiment,
} from '@/app/socialListening/utils/formatters';
import { findPostBySourceAndId } from '@/app/socialListening/utils/post-filter-utils';

/**
 * Builds the sentiment-editing wiring shared by MediaCard grids: replacing the footer
 * sentiment chip with an interactive SentimentSelect, and forwarding updates/loading state.
 * @param {() => Array<Object>} getPosts - Returns the current raw (unformatted) posts list to look up by source/sourceId
 * @param {Object} [options]
 * @param {(data: Object) => boolean} [options.shouldHide] - Extra per-card condition to keep the default read-only chip (e.g. YouTube compliance)
 */
export function useSentimentCardProps(getPosts, { shouldHide } = {}) {
  const { canManageSentiment, hasCustomSentiment } = useListeningPermissions();
  const { updateSentiment, isPending } = useOrganizationSentiment();

  function findPost(data) {
    return findPostBySourceAndId(getPosts(), data.source, data.sourceId);
  }

  function cardPropsFor(data) {
    if (!hasCustomSentiment.value || shouldHide?.(data)) {
      return data;
    }

    const post = findPost(data);
    if (!post) {
      return data;
    }

    return {
      ...data,
      metricItems: data.metricItems.filter((item) => item.label !== 'Sentiment'),
      sentiment: getPostSentiment(post).sentimentLabel,
      sentimentReadonly: !canManageSentiment.value || isPending(post),
      sentimentTooltip: formatSentimentAdjustmentTooltip(post.organizationSentiment),
    };
  }

  async function handleSentimentUpdate(data, label) {
    const post = findPost(data);
    if (!post) {
      return;
    }
    await updateSentiment(post, label);
  }

  function isSentimentLoading(data) {
    const post = findPost(data);
    return Boolean(post && isPending(post));
  }

  return { cardPropsFor, handleSentimentUpdate, isSentimentLoading };
}
